
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { getSortedPosts } from '@/data/blog-posts';

// Get sorted blog posts
const sortedBlogPosts = getSortedPosts();

// Show all posts by default
const POSTS_PER_PAGE = 6;

const Blog = () => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const indexOfLastPost = currentPage * POSTS_PER_PAGE;
  const indexOfFirstPost = indexOfLastPost - POSTS_PER_PAGE;
  const currentPosts = sortedBlogPosts.slice(indexOfFirstPost, indexOfLastPost);
  
  const totalPages = Math.ceil(sortedBlogPosts.length / POSTS_PER_PAGE);
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // Generate structured data for the blog
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Insights",
    "description": "Insights, updates, and resources for navigating the built environment's regulatory landscape",
    "url": "https://www.fordje.com/blog",
    "blogPost": sortedBlogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "image": post.coverImage,
      "author": {
        "@type": "Person",
        "name": post.author.name
      },
      "url": `https://www.fordje.com/blog/${post.slug}`
    }))
  };
  
  // Generate categories for keywords
  const allCategories = new Set<string>();
  sortedBlogPosts.forEach(post => {
    post.categories.forEach(category => {
      allCategories.add(category);
    });
  });
  const keywordsString = Array.from(allCategories).join(', ') + ', regulatory data, building codes, construction regulations';
  
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Insights - Regulatory Data for Construction</title>
        <meta name="description" content="Stay informed with the latest insights, updates, and resources for navigating the built environment's regulatory landscape." />
        <meta name="keywords" content={keywordsString} />
        <link rel="canonical" href="https://www.fordje.com/blog" />
        <meta property="og:title" content="Insights - Regulatory Data for Construction" />
        <meta property="og:description" content="Stay informed with the latest insights, updates, and resources for navigating the built environment's regulatory landscape." />
        <meta property="og:url" content="https://www.fordje.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.fordje.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Insights - Regulatory Data for Construction" />
        <meta name="twitter:description" content="Stay informed with the latest insights, updates, and resources for navigating the built environment's regulatory landscape." />
        <meta name="twitter:image" content="https://www.fordje.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <Header />
      <main className="flex-grow">
        <div className="container-custom section-padding">
          <div className="max-w-3xl mx-auto">
            <header>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Insights</h1>
              <p className="text-lg text-fordje-gray-dark mb-8">
                Insights, updates, and resources for navigating the built environment
              </p>
            </header>
            <Separator className="my-8" />

            {/* Featured Case Study */}
            <article className="group mb-12" itemScope itemType="https://schema.org/Article">
              <Link to="/report/discovery-tax" className="block hover:no-underline">
                <div className="mb-4 overflow-hidden rounded-lg">
                  <div className="w-full h-64 bg-gradient-to-br from-fordje-navy to-fordje-navy-light p-7 md:p-9 flex flex-col justify-center relative">
                    <Badge className="absolute top-4 left-4 bg-fordje-blue/20 text-fordje-blue border border-fordje-blue/30 font-mono text-[10px] uppercase tracking-[0.12em]">
                      Featured Report
                    </Badge>
                    <h2 className="font-ubuntu text-2xl md:text-3xl font-bold text-white leading-tight max-w-2xl">
                      The Discovery Tax: <span className="text-fordje-blue">$16,500</span> a month
                    </h2>
                    <p className="text-white/60 text-sm mt-2 max-w-xl">
                      Hardware costs collapsed. The regulatory work didn't.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mb-3">
                  <Badge variant="secondary" className="font-normal">C&amp;I Solar</Badge>
                  <Badge variant="secondary" className="font-normal">Regulatory Intelligence</Badge>
                </div>
                <h2 className="text-2xl font-bold mb-2 group-hover:text-fordje-blue transition-colors" itemProp="headline">
                  The Discovery Tax: $16,500 a month
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  <time dateTime="2026-06-01">June 2026</time> &bull; 6 min read
                </p>
                <p className="text-fordje-gray-dark" itemProp="description">
                  Hardware costs collapsed. The regulatory work didn't — and it's now the most stubborn cost in a commercial solar project. An anatomy of where that time goes and what it's worth.
                </p>
                <div className="mt-4 inline-flex items-center font-medium text-fordje-blue with-arrow">
                  Read the case study
                </div>
              </Link>
            </article>

            
            <div className="grid gap-10">
              {currentPosts.map((post) => {
                // Format the date for display
                const formattedDate = new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });

                return (
                  <article key={post.slug} className="group" itemScope itemType="https://schema.org/BlogPosting">
                    <Link to={`/blog/${post.slug}`} className="block hover:no-underline">
                      <div className="mb-4 overflow-hidden rounded-lg">
                        <img 
                          src={post.coverImage} 
                          alt={`Featured image for article: ${post.title}`}
                          itemProp="image"
                          className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="flex gap-2 mb-3">
                        {post.categories.map((category) => (
                          <Badge key={category} variant="secondary" className="font-normal">
                            {category}
                          </Badge>
                        ))}
                      </div>
                      <h2 className="text-2xl font-bold mb-2 group-hover:text-fordje-blue transition-colors" itemProp="headline">
                        {post.title}
                      </h2>
                      <p className="text-sm text-muted-foreground mb-4">
                        <time dateTime={post.date} itemProp="datePublished">
                          {formattedDate}
                        </time> • {post.readTime} min read
                      </p>
                      <p className="text-fordje-gray-dark" itemProp="description">{post.excerpt}</p>
                      <div className="mt-4 inline-flex items-center font-medium text-fordje-blue with-arrow">
                        Read more
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>
            
            {totalPages > 1 && (
              <nav aria-label="Blog pagination" className="mt-12">
                <Pagination>
                  <PaginationContent>
                    {currentPage > 1 && (
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => handlePageChange(currentPage - 1)} 
                          className="cursor-pointer"
                          aria-label="Go to previous page"
                        />
                      </PaginationItem>
                    )}
                    
                    {Array.from({ length: totalPages }).map((_, index) => {
                      const pageNumber = index + 1;
                      // Show current page, first, last, and pages around current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNumber}>
                            <PaginationLink
                              onClick={() => handlePageChange(pageNumber)}
                              isActive={pageNumber === currentPage}
                              className="cursor-pointer"
                              aria-label={`Go to page ${pageNumber}`}
                              aria-current={pageNumber === currentPage ? "page" : undefined}
                            >
                              {pageNumber}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      // Add ellipsis
                      if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                        return (
                          <PaginationItem key={pageNumber}>
                            <span className="flex h-9 w-9 items-center justify-center" aria-hidden="true">...</span>
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}
                    
                    {currentPage < totalPages && (
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => handlePageChange(currentPage + 1)} 
                          className="cursor-pointer"
                          aria-label="Go to next page"
                        />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </Pagination>
              </nav>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
